import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { flowRight as compose } from 'lodash';
import { gql } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';

import { useSelector, useDispatch } from 'react-redux';
import { Field, reduxForm, formValueSelector, reset } from 'redux-form';
import { useQuery } from '@apollo/client';
import FormGroup from 'react-bootstrap/lib/FormGroup';
import FormControl from 'react-bootstrap/lib/FormControl';
import Glyphicon from 'react-bootstrap/lib/Glyphicon';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement
} from '@stripe/react-stripe-js';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
// Component
import HouseRules from './HouseRules';
import Loader from '../../Loader';
import Link from '../../Link';
import Avatar from '../../Avatar';
import DriverInfo from './DriverInfo';

import messages from '../../../locale/jsonMessages';
import validate from './validate';
import imageOne from '/public/siteImages/showAllCars.svg';
import imageTwo from '/public/siteImages/stripImage.svg';
import paypalLogo from '/public/PaymentGateway/paypal.svg';
import stripeLogo from '/public/PaymentGateway/stripe.svg';
import pawapayLogo from '/public/PaymentGateway/pawapay.svg';
import showToaster from '../../../helpers/toasterMessages/showToaster';

import { makePayment } from '../../../actions/booking/makePayment';
import { processCardAction } from '../../../actions/PaymentIntent/processCardAction';
import { isRTL } from '../../../helpers/formatLocale';
import { COMMON_TEXT_COLOR } from '../../../constants/index';

import AskAIButton from '../../Common/AskAIButton/AskAIButton';
import { openAIModal } from '../../../actions/modalActions';
import CommonModal from '../../CommonModal/CommonModal';
import { formatTime } from '../../../helpers/formatting';
import { isRenterAIFeatureStatus } from '../../../helpers/aiFeatureStatusHelper';
import { dateFormat } from '../../../config';
import { formatLocalizedDate } from '../../../helpers/formatLocateDate';
import { parseOptionsPayload } from '../../../helpers/bookingOptionsPresentation';

import cs from '../../commonStyle.css';
import s from './Payment.css';

const createOptions = (isRTLLocale) => {
  return {
    style: {
      base: {
        color: COMMON_TEXT_COLOR,
        fontWeight: 400,
        fontFamily: 'inherit',
        textAlign: isRTLLocale ? 'right' : 'left',
        fontSize: '16px',
        fontSmoothing: 'antialiased',
        ':focus': {
          color: COMMON_TEXT_COLOR,
        },
        '::placeholder': {
          color: '#7D7D7D',
        },
        ':focus::placeholder': {
          color: '#7D7D7D',
        },
      },
      invalid: {
        color: '#303238',
        ':focus': {
          color: COMMON_TEXT_COLOR,
        },
        '::placeholder': {
          color: '#7D7D7D',
        },
      },
    }
  }
};

const paymentGatewayLogos = {
  1: paypalLogo,
  2: stripeLogo,
  3: pawapayLogo
};

const renderFormControlTextArea = ({ input, label, meta: { touched, error }, children, className }) => {
  const { formatMessage } = useIntl();

  return (
    <FormGroup className={cs.noMargin}>
      <FormControl
        {...input}
        className={className}
        componentClass="textarea"
        placeholder={label}
        rows={'6'}
      >
        {children}
      </FormControl>
      {touched && error && <span className={cs.errorMessage}>{formatMessage(error)}</span>}
    </FormGroup>
  );
};

// Keep Field component identities stable. Defining these inside PaymentForm
// remounts the underlying input/select after every redux-form update, which
// drops focus while the renter is typing or changing an option.
const renderFormControl = ({ input, placeholder, type, meta: { touched, error }, className, maxLength }) => {
  const { formatMessage } = useIntl();
  return (
    <FormGroup className={cx('inputFocusColorNone', cs.spaceBottom2)}>
      <FormControl {...input} placeholder={placeholder} type={type} className={className} maxLength={maxLength} />
      {touched && error && <span className={cs.errorMessage}>{formatMessage(error)}</span>}
    </FormGroup>
  );
};

const renderFormControlSelect = ({ input, meta: { touched, error }, children, className, disabled }) => {
  const { formatMessage } = useIntl();
  return (
    <FormGroup className={cs.spaceBottom3}>
      <FormControl componentClass="select" {...input} className={className} disabled={disabled === true}>
        {children}
      </FormControl>
      {touched && error && <span className={cs.errorMessage}>{formatMessage(error)}</span>}
    </FormGroup>
  );
};

const PaymentForm = (props) => {
  const {
    houseRules,
    hostDisplayName,
    allowedPersonCapacity,
    initialValues,
    hostPicture,
    hostProfileId,
    hostJoined,
    listId,
    handleSubmit,
    submitting,
    error,
    securityDepositStatus,
    change
  } = props;

  const intl = useIntl();
  const { formatMessage, locale } = intl;
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const selectedPaymentCurrency = useSelector(state => selector(state, 'paymentCurrency'));

  const paymentCurrencyList = useSelector(state => state?.currency?.availableCurrencies);
  const paymentLoading = useSelector(state => state?.book?.paymentLoading);
  const paymentFormErrors = useSelector(state => state?.form?.PaymentForm?.syncErrors || {});
  const paymentSubmitFailed = useSelector(state => state?.form?.PaymentForm?.submitFailed);
  const paymentType = useSelector(state => selector(state, 'paymentType'));
  useEffect(() => {
    if (Number(paymentType) === 1 && !['EUR', 'USD'].includes(selectedPaymentCurrency)) change('paymentCurrency', 'EUR');
  }, [paymentType, selectedPaymentCurrency, change]);
  const pawapayCountry = useSelector(state => selector(state, 'pawapayCountry'));
  const pawapayCurrency = useSelector(state => selector(state, 'pawapayCurrency'));
  const pawapayProvider = useSelector(state => selector(state, 'pawapayProvider'));
  const checkIn = useSelector(state => selector(state, 'checkIn'));
  const checkOut = useSelector(state => selector(state, 'checkOut'));
  const startTime = useSelector(state => selector(state, 'startTime'));
  const endTime = useSelector(state => selector(state, 'endTime'));
  const message = useSelector(state => selector(state, 'message'));
  const aiMessageContent = useSelector(state => reservationMessageSelector(state, 'aiMessageContent'))
  const renterBookingRequest = useSelector(state => state?.siteSettings?.data?.renterBookingRequest)
  const isAiFeatureEnabled = useSelector(state => state.siteSettings.data?.aiTextGeneration);
  const renterServicePlanAIFeatureStatus = useSelector(state => state?.account?.data?.renterServicePlanAIFeatureStatus);
  const aiRenterAccess = useSelector(state => state?.siteSettings?.data?.aiRenterAccess);


  const [paymentStatus, setPaymentStatus] = useState(2);
  const [load, setLoad] = useState(true);
  const [pawapayOptions, setPawapayOptions] = useState([]);
  const [pawapayLoading, setPawapayLoading] = useState(false);
  const [pawapayError, setPawapayError] = useState('');
  const [pawapayPendingReservationId, setPawapayPendingReservationId] = useState(null);
  const [pawapayRetry, setPawapayRetry] = useState(0);

  const { data: paymentMethodsData } = useQuery(gql`
    query getPaymentMethods {
      getPaymentMethods {
        id
        name
        isEnable
        paymentType
        paymentName
        paymentSubtitle
        status
      }
    }
  `, { fetchPolicy: 'network-only', ssr: false });

  const paymentMethods = paymentMethodsData?.getPaymentMethods || [];
  // getPaymentMethods defaults to the checkout operation, so every returned gateway
  // is immediately available to the renter. PawaPay country availability is only
  // needed after the renter chooses it.
  const availablePaymentMethods = paymentMethods;

  useEffect(() => {
    if (!availablePaymentMethods.length) return;
    if (!availablePaymentMethods.some(method => Number(method.id) === Number(paymentStatus))) {
      const selected = availablePaymentMethods[0]?.id || 0;
      setPaymentStatus(selected);
      change('paymentType', selected);
    }
  }, [availablePaymentMethods, paymentStatus, change]);

  useEffect(() => {
    if (paymentStatus !== 3 || pawapayOptions.length) return undefined;
    let active = true;
    setPawapayLoading(true);
    fetch('/pawapay/deposit-options', { credentials: 'include' })
      .then(response => response.json())
      .then(result => {
        if (!active) return;
        if (result.status !== 200 || !result.countries?.length) throw new Error(result.errorMessage || 'Aucun pays PawaPay actif.');
        setPawapayOptions(result.countries);
        const selected = result.countries.find(item => item.country === result.recommendedCountry) || result.countries[0];
        change('pawapayCountry', selected.country);
        change('pawapayCurrency', selected.currencies[0].currency);
        change('pawapayProvider', selected.currencies[0].providers[0]?.provider || '');
        setPawapayError('');
      })
      .catch(error => active && setPawapayError(error.message))
      .finally(() => active && setPawapayLoading(false));
    return () => { active = false; };
  }, [paymentStatus, pawapayOptions.length, pawapayRetry, change]);

  useEffect(() => {
    if (!pawapayCountry || !pawapayOptions.length) return;
    const selected = pawapayOptions.find(item => item.country === pawapayCountry);
    if (selected && !selected.currencies.some(item => item.currency === pawapayCurrency)) change('pawapayCurrency', selected.currencies[0].currency);
  }, [pawapayCountry, pawapayCurrency, pawapayOptions, change]);

  useEffect(() => {
    if (!pawapayCountry || !pawapayCurrency || !pawapayOptions.length) return;
    const selectedCountry = pawapayOptions.find(item => item.country === pawapayCountry);
    const selectedCurrency = selectedCountry?.currencies.find(item => item.currency === pawapayCurrency);
    if (selectedCurrency && !selectedCurrency.providers.some(item => item.provider === pawapayProvider)) {
      change('pawapayProvider', selectedCurrency.providers[0]?.provider || '');
    }
  }, [pawapayCountry, pawapayCurrency, pawapayProvider, pawapayOptions, change]);

  useEffect(() => {
    if (!pawapayPendingReservationId) return undefined;
    let active = true;
    const checkStatus = () => fetch(`/pawapay/deposit-status/${pawapayPendingReservationId}`, { credentials: 'include' })
      .then(response => response.json())
      .then(result => {
        if (!active) return;
        if (result.paymentStatus === 'COMPLETED') window.location.assign(`/users/trips/itinerary/${pawapayPendingReservationId}`);
        if (['FAILED', 'REJECTED'].includes(result.paymentStatus)) {
          setPawapayError(formatMessage(messages.pawapayPaymentRejected));
          setPawapayPendingReservationId(null);
        }
        if (result.status !== 200) {
          setPawapayError(result.errorMessage || 'La vérification du paiement Mobile Money a échoué.');
          setPawapayPendingReservationId(null);
        }
      })
      .catch(() => undefined);
    checkStatus();
    const interval = setInterval(checkStatus, 5000);
    return () => { active = false; clearInterval(interval); };
  }, [pawapayPendingReservationId, formatMessage]);

  useEffect(() => {
    setLoad(false);
    const timer = setTimeout(() => setLoad(true), 1);
    return () => clearTimeout(timer);
  }, [locale]);

  const renderpaymentCurrencies = () => {
    let rows = [];
    ['EUR', 'USD'].forEach(currency => rows.push(<option key={currency} value={currency} disabled={paymentLoading}>{currency}</option>));
    return rows;
  };

  const handleClick = () => {
    dispatch(reset('BookingForm'));
  };


  const handleOpenAIModal = () => {
    dispatch(openAIModal())
  }

  const onSubmit = async (values) => {
    if (!availablePaymentMethods.some(method => Number(method.id) === Number(values.paymentType))) return;
    if (Number(values.paymentType) === 1 && !['EUR', 'USD'].includes(values.paymentCurrency)) {
      change('paymentCurrency', 'EUR');
      return;
    }
    if (Number(values.paymentType) === 3 && (!values.pawapayCountry || !values.pawapayCurrency || !values.pawapayProvider || !values.pawapayPhoneNumber)) {
      showToaster({ messageId: 'failedError', toasterType: 'error', requestMessage: pawapayError || 'Renseignez votre numéro et votre opérateur Mobile Money.' });
      return;
    }
    if (Number(values.paymentType) === 3) setPawapayError('');
    let paymentType = values.paymentType, paymentCurrency, month;
    let monthValue, dateValue, dateOfBirth;
    let today, birthDate, age, monthDifference, dobDate;
    let dateOfMonth = Number(values.month) + 1;

    dobDate = values.year + '/' + dateOfMonth + '/' + values.day;
    paymentCurrency = values.paymentType == 1 ? values.paymentCurrency : null;
    month = values.month ? Number(values.month) + 1 : null;
    monthValue = Number(values.month) > 8 ? Number(month) : '0' + month;
    dateValue = values.day > 9 ? values.day : '0' + values.day;
    dateOfBirth = monthValue + "-" + dateValue + "-" + values.year;
    today = new Date();
    birthDate = new Date(dobDate);
    age = today.getFullYear() - birthDate.getFullYear();
    monthDifference = today.getMonth() - birthDate.getMonth();

    if (values.year) {
      if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) age--;
      if (age < 18) {
        showToaster({ messageId: 'checkAge', toasterType: 'error' });
        return false;
      }
    }

    if (!values.isHostServiceEligible) {
      showToaster({ messageId: 'hostServiceFeeError', toasterType: 'error' })
      return;
    }

    const query = `query checkReservation ($checkIn: String,$checkOut: String,$listId: Int, $securityDepositStatus: String){
      checkReservation(checkIn: $checkIn, checkOut:$checkOut, listId:$listId, securityDepositStatus: $securityDepositStatus){
        id
        listId
        hostId
        guestId
        checkIn
        checkOut
        status
      }
    }`;

    const params = {
      listId: values.listId,
      checkIn: values.checkIn,
      checkOut: values.checkOut,
      securityDepositStatus: securityDepositStatus
    };

    const resp = await fetch('/graphql', {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query,
        variables: params,
      }),
      credentials: 'include',
    });

    const { data } = await resp.json();

    if (data?.checkReservation && data?.checkReservation?.status == "200") {
      let msg = '', paymentMethodId, createPaymentMethod;

      if (paymentType == 2) {
        if (!stripe || !elements) return;

        let card = elements.getElement(CardNumberElement);

        createPaymentMethod = await stripe.createPaymentMethod({
          type: 'card',
          card,
        });

        if (createPaymentMethod?.paymentMethod) {
          paymentMethodId = createPaymentMethod.paymentMethod.id;
        }
      }

      if (createPaymentMethod?.error && createPaymentMethod?.error?.message && paymentType == 2) {
        msg = createPaymentMethod.error.message;
        showToaster({ messageId: 'invalidError', toasterType: 'error', requestMessage: msg });
      } else {
        if (Number(values.paymentType) == 2 && !values.zipcode) {
          showToaster({ messageId: 'inCompleteZipCode', toasterType: 'error' });
          return;
        }

        const paymentResult = await dispatch(makePayment(
          values?.listId,
          values?.listTitle,
          values?.hostId,
          values?.guestId,
          values?.checkIn,
          values?.checkOut,
          values?.guests,
          values?.message,
          values?.basePrice,
          parseOptionsPayload(values?.optionsPayload),
          values?.currency,
          values?.discount,
          values?.discountType,
          values?.guestServiceFee,
          values?.hostServiceFee,
          values?.total,
          values?.bookingType,
          paymentCurrency,
          paymentType,
          values?.guestEmail,
          values?.bookingSpecialPricing,
          values?.isSpecialPriceAssigned,
          values?.isSpecialPriceAverage,
          values?.dayDifference,
          values?.startTime,
          values?.endTime,
          values?.licenseNumber,
          values?.firstName,
          values?.middleName,
          values?.lastName,
          dateOfBirth,
          values?.country,
          paymentMethodId,
          values?.securityDeposit,
          values?.hostServiceFeeType,
          values?.hostServiceFeeValue,
          values?.guestServiceFeeType,
          values?.guestServiceFeeValue,
          values?.threadId,
          values?.promoId,
          values?.promoDiscount,
          values?.pawapayCountry,
          values?.pawapayCurrency,
          values?.pawapayPhoneNumber,
          values?.pawapayProvider
        ));
        const { status, paymentIntentSecret, reservationId, errorMessage } = paymentResult || {};

        if (paymentType == 3 && status === 200 && reservationId) setPawapayPendingReservationId(reservationId);
        if (paymentType == 3 && status !== 200) {
          setPawapayError(errorMessage || formatMessage(messages.pawapayPaymentFailedDescription));
        }


        if (status == 400 && paymentType == 2) {
          const cardAction = await stripe.handleCardAction(
            paymentIntentSecret,
          );
          let amount = values?.total + values?.guestServiceFee, confirmPaymentIntentId;

          if (cardAction?.paymentIntent && cardAction?.paymentIntent?.id) {
            confirmPaymentIntentId = cardAction?.paymentIntent?.id;
            await dispatch(processCardAction(
              reservationId,
              values?.listId,
              values?.hostId,
              values?.guestId,
              values?.listTitle,
              values?.guestEmail,
              amount,
              values?.currency,
              confirmPaymentIntentId
            ));
          } else {
            if (cardAction?.error && cardAction?.error?.message) {
              msg = cardAction.error.message;
              showToaster({ messageId: 'invalidError', toasterType: 'error', requestMessage: msg });
            }
          }
        }
      }
    } else if (data?.checkReservation?.status == "404") {
      showToaster({ messageId: 'securityDepositChange', toasterType: 'error' });
      return;
    } else {
      showToaster({ messageId: 'datesNotAvailable', toasterType: 'error' });
    }
  };

  const handlePayment = (methodId) => {
    const selectedMethodId = Number(methodId);
    if (selectedMethodId === 3 && paymentStatus !== 3) setPawapayError('');
    setPaymentStatus(selectedMethodId);
    change('paymentType', selectedMethodId);
  };

  const pawapayPreparing = paymentStatus === 3 && (pawapayLoading || (!pawapayOptions.length && !pawapayError));

  const paymentFieldLabels = {
    licenseNumber: messages.licenseNumber,
    firstName: messages.firstName,
    lastName: messages.lastName,
    country: messages.country,
    day: messages.dateOfBirth,
    month: messages.dateOfBirth,
    year: messages.dateOfBirth,
    message: messages.aboutYourTrip,
    paymentCurrency: messages.chooseCurrency,
    pawapayCountry: messages.country,
    pawapayCurrency: messages.paymentCurrency,
    pawapayProvider: messages.paymentMethod,
    name: messages.name,
    cardNumber: messages.paymentCardNumber,
    expiryDate: messages.cardExpires,
    expiryYear: messages.cardExpires,
    cvv: messages.cvv,
    zipcode: messages.zipcode
  };
  const paymentValidationErrors = Object.entries(paymentFormErrors).reduce((items, [field, validationMessage]) => {
    const label = paymentFieldLabels[field];
    if (!label || !validationMessage || items.some(item => item.label.id === label.id && item.message.id === validationMessage.id)) return items;
    items.push({ label, message: validationMessage });
    return items;
  }, []);

  const joinedDate = hostJoined != null ? formatLocalizedDate(hostJoined, locale, dateFormat?.monthYearFormat) : '';

  let checkInDate, checkOutDate;
  checkInDate =
    checkIn != null
      ? moment(moment(checkIn).format("YYYY-MM-DD")).locale(locale).format('L')
      : "";
  checkOutDate =
    checkOut != null
      ? moment(moment(checkOut).format("YYYY-MM-DD")).locale(locale).format('L')
      : "";



  let formattedStartTime = formatTime(startTime);
  let formattedEndTime = formatTime(endTime);
  const formValues = {
    checkIn: `${checkInDate}, ${formattedStartTime}`,
    checkOut: `${checkOutDate}, ${formattedEndTime}`,
    hostName: hostDisplayName,
    aiMessageContent: aiMessageContent,
    aiMessage: message
  }

  let isRenterBookingRequestStatus = isRenterAIFeatureStatus({
    isRenterAIEnabled: renterBookingRequest,
    isGlobalAIEnabled: isAiFeatureEnabled,
    hasActiveServicePlan: renterServicePlanAIFeatureStatus,
    servicePlanUsers: aiRenterAccess
  })
  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <h1 className={cx(cs.commonTitleText, cs.paddingBottom4)}><FormattedMessage {...messages.reviewAndPay} /></h1>
        <h3 className={cx(s.titleText, cs.paddingBottom2)}>
          1.{' '}<FormattedMessage {...messages.licenseInfo} />
        </h3>
        <h4 className={cx(cs.commonContentText, cs.paddingBottom4)}>
          <FormattedMessage {...messages.aboutLicenseContent} />
        </h4>
        <DriverInfo />
        <hr className={s.horizondalLine} />
        <h5 className={cx(s.titleText, cs.paddingBottom2)}>
          2.{' '}<FormattedMessage {...messages.aboutYourTrip} /><sup className='isRequiredStar'>*</sup>
        </h5>
        <h5 className={cx(cs.commonContentText, cs.paddingBottom4)}>
          <FormattedMessage {...messages.sayHello} />
        </h5>
        <div className={cx(s.avatarImageGrid, cs.spaceBottom4)}>
          <Avatar
            source={hostPicture}
            type={"small"}
            height={80}
            width={80}
            title={hostDisplayName}
            className={cx(cs.profileAvatarLink, cs.profileAvatarLinkPayment)}
            withLink
            linkClassName={cs.displayinlineBlock}
            profileId={hostProfileId}
          />
          <div className={cx(s.textSection, 'viewListingTextSectionRTL')}>
            <a href={"/users/show/" + hostProfileId} target={'_blank'} className={cx(cs.commonSubTitleText, cs.siteTextColor, cs.fontWeightBold)}>
              {formatMessage(messages.hostedBy)} {' '}  <span className={cs.siteLinkColor}>{hostDisplayName}</span>
            </a>
            <h4 className={cx(cs.commonContentText, cs.fontWeightNormal, cs.paddingTop1)}>
              {formatMessage(messages.joinedIn)}{' '}{joinedDate}
            </h4>
          </div>
        </div>
        <Field
          name="message"
          component={renderFormControlTextArea}
          label={formatMessage(messages.descriptionInfo)}
          className='commonTextAreaField'
        />
        <CommonModal
          title={formatMessage(messages.generateWithAI)}
          formName={"reservationMessage"}
          formValues={formValues}
        />

        <AskAIButton
          containerClass={cs.spaceTop2}
          btnText={formatMessage(messages.generateWithAI)}
          onClick={handleOpenAIModal}
          isButtonVisible={isRenterBookingRequestStatus}
        />
        <hr className={s.horizondalLine} />
        {houseRules?.length > 0 && <>
          <HouseRules
            hostDisplayName={hostDisplayName}
            houseRules={houseRules}
          />
          <hr className={s.horizondalLine} />
        </>}
        <div>
          <h5 className={cx(s.titleText, cs.paddingBottom2)}>4.{' '}<FormattedMessage {...messages.payment} /></h5>
          {availablePaymentMethods.length === 0 && !pawapayLoading && <p role="status">{formatMessage(messages.noPaymentFound)}</p>}
          {availablePaymentMethods.length > 0 &&
            <div className={s.paymentMethodsSection} role="radiogroup" aria-label={formatMessage(messages.PaymentMethodText)}>
              <h6 className={cx(cs.commonContentText, cs.paddingBottom3)}><FormattedMessage {...messages.paymentText} /></h6>
              <div className={s.paymentMethodsGrid}>
                {availablePaymentMethods.map(method => {
                  const methodId = Number(method.id);
                  const isSelected = paymentStatus === methodId;
                  return (
                    <button
                      key={method.id}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      className={cx(s.paymentMethodCard, { [s.paymentMethodCardSelected]: isSelected })}
                      onClick={() => handlePayment(methodId)}
                    >
                      <img src={paymentGatewayLogos[methodId]} alt="" className={s.paymentMethodLogo} />
                      <span className={s.paymentMethodName}>
                        <span>{method.paymentName || method.name}</span>
                        {method.paymentSubtitle && <small className={s.paymentMethodSubtitle}>{method.paymentSubtitle}</small>}
                      </span>
                      <span className={s.paymentMethodSelection} aria-hidden="true" />
                    </button>
                  );
                })}
              </div>
            </div>
          }
          {paymentStatus == 2 ? (!load ? <Loader /> :
            <>
              <div className={cx('placeHolderFont', s.cardSection)}>
                <div>
                  <label><FormattedMessage {...messages.paymentCardNumber} /> <sup className='isRequiredStar'>*</sup></label>
                  <CardNumberElement
                    options={{ placeholder: "4242 4242 4242 4242", ...createOptions(isRTL(locale)) }}
                    className={cx(s.stripCardInputContainer, s.stripCardAccountInput, 'stripCardInputContainerRtl')}
                  />
                </div>
                <div className={s.stripCardGridBox}>
                  <div>
                    <label><FormattedMessage {...messages.cardExpires} /> <sup className='isRequiredStar'>*</sup></label>
                    <CardExpiryElement
                      placeholder="MM / YY"
                      {...createOptions(isRTL(locale))}
                      className={cx(s.stripCardInputContainer, s.stripCardExpiryInput, 'stripCardInputContainerRtl')}
                    />
                  </div>
                  <div className={cx(s.datePadding, s.cvvNoPadding)}>
                    <label><FormattedMessage {...messages.cvv} /> <sup className='isRequiredStar'>*</sup></label>
                    <CardCvcElement
                      placeholder="_ _ _"
                      {...createOptions(isRTL(locale))}
                      className={cx(s.stripCardInputContainer, s.stripCardCvvInput, 'stripCardInputContainerRtl')}
                    />
                  </div>
                  <div>
                    <label><FormattedMessage {...messages.zipcode} /> <sup className='isRequiredStar'>*</sup></label>
                    <div className={cx(s.stripCardInputContainer, s.stripCardOPostalInput, 'stripCardOPostalInputRtl')}>
                      <Field
                        name="zipcode"
                        component={renderFormControl}
                        placeholder={formatMessage(messages.zipcode)}
                        maxLength={30}
                      />
                    </div>
                  </div>
                </div>
                <div className={cx(s.tableFlex, s.flexWrap, s.gap12)}>
                  <img src={imageOne} className={s.stripeImg} />
                  <img src={imageTwo} className={s.stripeImg} />
                </div>
              </div>
            </>
          ) : <span></span>}
          {paymentStatus == 1 &&
            <>
              <Field name="paymentCurrency" disabled={paymentType == 2} component={renderFormControlSelect} className='commonSelectField' >
                <option disabled={paymentLoading} value="">{formatMessage(messages.chooseCurrency)}</option>
                {renderpaymentCurrencies()}
              </Field>
              <p className={cx(cs.commonContentText, cs.paddingBottom4)}>
                <FormattedMessage {...messages.loginInfo} />
              </p>
            </>}
          {paymentStatus == 3 &&
            <div className={cs.paddingBottom4}>
              {pawapayPreparing && <div className={s.pawapayPreparing} role="status" aria-live="polite">
                <span className={s.pawapaySpinner} aria-hidden="true" />
                <div>
                  <strong>{formatMessage(messages.pawapayPreparingTitle)}</strong>
                  <p>{formatMessage(messages.pawapayPreparingDescription)}</p>
                </div>
              </div>}
              {!pawapayLoading && pawapayOptions.length > 0 && <>
                <label className="commonLabelText">Pays du portefeuille Mobile Money</label>
                <Field name="pawapayCountry" component={renderFormControlSelect} className="commonSelectField">
                  {pawapayOptions.map(item => <option key={item.country} value={item.country}>
                    {(typeof item.displayName === 'string' ? item.displayName : (item.displayName?.[locale] || item.displayName?.fr || item.displayName?.en)) || item.country} (+{item.prefix})
                  </option>)}
                </Field>
                <label className="commonLabelText">Numéro Mobile Money</label>
                <div className={s.pawapayPhoneRow}>
                  <span className={s.pawapayCountryCode}>+{pawapayOptions.find(item => item.country === pawapayCountry)?.prefix || ''}</span>
                  <div className={s.pawapayPhoneInput}>
                    <Field name="pawapayPhoneNumber" component={renderFormControl} className="commonInput" type="tel" placeholder="Numéro sans indicatif" />
                  </div>
                </div>
                <label className="commonLabelText">Devise du paiement</label>
                <Field name="pawapayCurrency" component={renderFormControlSelect} className="commonSelectField">
                  {(pawapayOptions.find(item => item.country === pawapayCountry)?.currencies || []).map(item =>
                    <option key={item.currency} value={item.currency}>{item.currency}</option>)}
                </Field>
                <label className="commonLabelText">Opérateur Mobile Money</label>
                <Field name="pawapayProvider" component={renderFormControlSelect} className="commonSelectField">
                  {(pawapayOptions.find(item => item.country === pawapayCountry)?.currencies.find(item => item.currency === pawapayCurrency)?.providers || []).map(item =>
                    <option key={item.provider} value={item.provider}>{item.displayName || item.provider}</option>)}
                </Field>
                <p className={cx(cs.commonContentText, cs.paddingTop1)}>
                  Vous recevrez la demande de paiement directement sur ce numéro. Le statut du dépôt est vérifié automatiquement.
                </p>
              </>}
            </div>}
          {paymentSubmitFailed && paymentValidationErrors.length > 0 &&
            <div className={s.paymentValidationSummary} role="alert">
              <ul>
                {paymentValidationErrors.map(({ label, message }) => (
                  <li key={`${label.id}-${message.id}`}>
                    <strong>{formatMessage(label)}</strong> : {formatMessage(message)}
                  </li>
                ))}
              </ul>
            </div>
          }
          {paymentStatus === 3 && pawapayPendingReservationId &&
            <div className={s.pawapayPending} role="status" aria-live="polite">
              <span className={s.pawapayPendingIcon} aria-hidden="true"><Glyphicon glyph="phone" /></span>
              <div className={s.pawapayFeedbackContent}>
                <strong>{formatMessage(messages.pawapayConfirmPhoneTitle)}</strong>
                <p>{formatMessage(messages.pawapayConfirmPhoneDescription)}</p>
                <span className={s.pawapayChecking}><span className={s.pawapaySpinner} aria-hidden="true" />{formatMessage(messages.pawapayCheckingPayment)}</span>
              </div>
            </div>}
          {paymentStatus === 3 && pawapayError && !pawapayLoading &&
            <div className={s.pawapayError} role="alert">
              <span className={s.pawapayErrorIcon} aria-hidden="true"><Glyphicon glyph="exclamation-sign" /></span>
              <div className={s.pawapayFeedbackContent}>
                <strong>{formatMessage(pawapayOptions.length ? messages.pawapayPaymentFailedTitle : messages.pawapayOptionsFailedTitle)}</strong>
                <p>{pawapayError}</p>
                {!pawapayOptions.length && <button type="button" className={s.pawapayRetryButton} onClick={() => { setPawapayError(''); setPawapayRetry(value => value + 1); }}>
                  {formatMessage(messages.pawapayRetryLoading)}
                </button>}
              </div>
            </div>}
          <div className={cx('doubleBtnFlexEnd', cs.spaceTop4)}>
            {!paymentLoading && <>
              <Link
                to={"/cars/" + listId}
                className={cx(cs.btnPrimaryBorder, cs.displayinlineBlock,)}
                onClick={handleClick}
              >
                <FormattedMessage {...messages.cancel} />
              </Link>
            </>}
            {paymentLoading && <>
              <a
                href="javascript:void(0)"
                className={cx(cs.btnPrimaryBorder, cs.displayinlineBlock, s.cancelLinkText, {
                  [s.disabledLink]: submitting == true,
                })}
              >
                <FormattedMessage {...messages.cancel} />
              </a>
            </>}
            <Loader
              type={"button"}
              buttonType={"submit"}
              className={cx(cs.btnPrimary, 'loaderButtonFlex')}
              disabled={submitting || error || pawapayPendingReservationId || !availablePaymentMethods.length || (paymentStatus === 3 && (pawapayLoading || !pawapayCountry || !pawapayCurrency || !pawapayProvider))}
              show={paymentLoading}
              label={formatMessage(messages.payNow)}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

PaymentForm.propTypes = {
  houseRules: PropTypes.arrayOf(PropTypes.shape({
    listsettings: PropTypes.shape({
      itemName: PropTypes.string.isRequired
    })
  })),
  hostDisplayName: PropTypes.string.isRequired,
  allowedPersonCapacity: PropTypes.number.isRequired,
  initialValues: PropTypes.shape({
    listId: PropTypes.number.isRequired,
    listTitle: PropTypes.string.isRequired,
    hostId: PropTypes.string.isRequired,
    guestId: PropTypes.string.isRequired,
    checkIn: PropTypes.object.isRequired,
    checkOut: PropTypes.object.isRequired,
    guests: PropTypes.number.isRequired,
    basePrice: PropTypes.number.isRequired,
    optionsPayload: PropTypes.string,
    currency: PropTypes.string.isRequired,
    weeklyDiscount: PropTypes.number,
    monthlyDiscount: PropTypes.number,
    paymentType: PropTypes.number
  }).isRequired,
  paymentCurrencyList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    symbol: PropTypes.string.isRequired,
    isEnable: PropTypes.bool.isRequired,
    isPayment: PropTypes.bool.isRequired
  })),
  paymentLoading: PropTypes.bool,
  hostPicture: PropTypes.string,
  hostProfileId: PropTypes.string,
  hostJoined: PropTypes.string,
  listId: PropTypes.number,
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  error: PropTypes.object,
  securityDepositStatus: PropTypes.string
};

PaymentForm.defaultProps = {
  paymentCurrencyList: [],
  paymentLoading: false
};

const selector = formValueSelector('PaymentForm');
const reservationMessageSelector = formValueSelector("ReservationMessage");


const mapState = (state) => ({
  securityDepositStatus: state?.siteSettings?.data?.securityDepositPreference
});

export default compose(
  withStyles(cs, s),
  reduxForm({
    form: 'PaymentForm',
    validate
  }),
  connect(mapState)
)(PaymentForm);
